import React, { useState } from 'react';
import { Box, TextField, Typography, Container, Paper, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import API_ENDPOINTS from '../config/apiConfig';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/i18n';
import FixedSnackbar from './FixedSnackbar';
import LoadingButton from './LoadingButton';


function ChangePassword() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validatePassword = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!newPassword) {
      newErrors.newPassword = t('changepassword_error_new_password');
    } else if (!passwordRegex.test(newPassword)) {
      newErrors.newPassword = t('changepassword_error_password_strength');
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('changepassword_error_mismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        // Call the logout API after password change
        await fetch(API_ENDPOINTS.LOGOUT, {
          method: 'GET',
          credentials: 'include',
        });

        // Clear authentication state and redirect to login
        localStorage.removeItem('isAuthenticated'); // Clear any stored authentication state
        setSnackbarOpen(true);

        setTimeout(() => {
          window.location.href = '/login'; // Force a hard redirect to the login page
        }, 3000);
      } else {
        setMessage(data.message || t('changepassword_error_failed'));
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage(t('changepassword_error_general'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" style={{ marginTop: '50px' }}>
      <Paper elevation={6} style={{ padding: '30px', borderRadius: '15px', position: 'relative' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            size="small"
            sx={{ mr: 1 }}
            aria-label="back to dashboard"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body1"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 500 }}
            onClick={() => navigate('/dashboard')}
          >
            {t('back') || 'Back'}
          </Typography>
        </Box>
        <Typography variant="h4" align="center" gutterBottom>
          {t('changepassword_title')}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '20px' }}>
          {t('changepassword_requirements')}
        </Typography>
        <TextField
          fullWidth
          label={t('changepassword_current_password')}
          type="password"
          variant="outlined"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label={t('changepassword_new_password')}
          type="password"
          variant="outlined"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          error={!!errors.newPassword}
          helperText={errors.newPassword}
        />
        <TextField
          fullWidth
          label={t('changepassword_confirm_password')}
          type="password"
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />
        <LoadingButton
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleChangePassword}
          loading={submitting}
          loadingLabel={t('changepassword_button')}
          style={{ marginTop: '20px' }}
        >
          {t('changepassword_button')}
        </LoadingButton>
        {message && (
          <Typography color="error" align="center" style={{ marginTop: '20px' }}>
            {message}
          </Typography>
        )}
      </Paper>
      <FixedSnackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={t('changepassword_success')}
        severity="success"
      />
    </Container>
  );
}

export default ChangePassword;